import { useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { mediaUrl } from "@/lib/api";
import { useCart, priceLabel, hasVariants } from "@/lib/cart";

export default function FeaturedCarousel({ products = [] }) {
  const trackRef = useRef(null);
  const { addItem } = useCart();
  const navigate = useNavigate();

  const scroll = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.85, behavior: "smooth" });
  };

  if (!products.length) return null;

  return (
    <div className="relative" data-testid="featured-carousel">
      <button
        type="button"
        aria-label="Previous"
        data-testid="featured-prev"
        onClick={() => scroll(-1)}
        className="hidden sm:flex absolute -left-3 top-1/2 -translate-y-1/2 z-10 w-11 h-11 items-center justify-center bg-navy text-white hover:bg-copper hover:text-navy transition-colors shadow-lg"
      >
        <ChevronLeft size={22} />
      </button>

      <div
        ref={trackRef}
        className="flex gap-5 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-3 px-0.5 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
      >
        {products.map((p) => (
          <div
            key={p.id}
            data-testid={`featured-card-${p.slug}`}
            className="snap-start shrink-0 w-[220px] sm:w-[250px] bg-white border border-tan/40 hover:border-navy transition-colors flex flex-col"
          >
            <Link to={`/shop/${p.slug}`} data-testid={`featured-link-${p.slug}`} className="block aspect-square bg-white overflow-hidden">
              {p.image_url && (
                <img src={mediaUrl(p.image_url)} alt={p.name} className="w-full h-full object-contain p-4 hover:scale-[1.04] transition-transform duration-500" />
              )}
            </Link>
            <div className="px-4 pb-4 pt-2 text-center flex flex-col flex-1">
              <Link to={`/shop/${p.slug}`} className="font-serif text-base text-navy leading-snug hover:text-copper transition-colors line-clamp-2 min-h-[2.6em]">
                {p.name}
              </Link>
              <div className="mt-1.5 text-sm text-copper font-medium">{priceLabel(p)}</div>
              <button
                type="button"
                data-testid={`featured-add-${p.slug}`}
                onClick={() => {
                  if (hasVariants(p)) { navigate(`/shop/${p.slug}`); return; }
                  addItem(p, 1);
                  toast.success(`${p.name} added to cart`);
                }}
                className="mt-3 w-full bg-navy text-white py-2.5 text-xs uppercase tracking-[0.18em] hover:bg-charcoal transition-colors"
              >
                {hasVariants(p) ? "Choose Options" : "Add to Cart"}
              </button>
            </div>
          </div>
        ))}
      </div>

      <button
        type="button"
        aria-label="Next"
        data-testid="featured-next"
        onClick={() => scroll(1)}
        className="hidden sm:flex absolute -right-3 top-1/2 -translate-y-1/2 z-10 w-11 h-11 items-center justify-center bg-navy text-white hover:bg-copper hover:text-navy transition-colors shadow-lg"
      >
        <ChevronRight size={22} />
      </button>
    </div>
  );
}
